import { Endianness } from "../Parser/endianness";
/**
 * Data serializer only for strictly 4 byte aligned binary data
 *
 * Data always serialized in little endian
 */
export class AlignedDataSerializer extends Endianness {
    _arrayBuffer;
    _uint8Ptr;
    _uint16Ptr;
    _uint32Ptr;
    _int32Ptr;
    _float32Ptr;
    _encoder;
    _offset;
    /**
     * Creates Aligned data serializer
     * @param arrayBuffer ArrayBuffer to serialize data
     */
    constructor(arrayBuffer) {
        super();
        this._arrayBuffer = arrayBuffer;
        this._uint8Ptr = new Uint8Array(arrayBuffer);
        this._uint16Ptr = new Uint16Array(arrayBuffer);
        this._uint32Ptr = new Uint32Array(arrayBuffer);
        this._int32Ptr = new Int32Array(arrayBuffer);
        this._float32Ptr = new Float32Array(arrayBuffer);
        this._encoder = new TextEncoder();
        this._offset = 0;
    }
    /**
     * Current offset in the buffer
     */
    get offset() {
        return this._offset;
    }
    set offset(value) {
        this._offset = value;
    }
    /**
     * Writes a uint8 value
     * @param value Uint8 value to write
     */
    setUint8(value) {
        this._uint8Ptr[this._offset >> 0 /* Uint8.Shift */] = value;
        this._offset += 1 << 0 /* Uint8.Shift */;
    }
    /**
     * Writes a uint8 array
     * @param values Uint8 array to write
     */
    setUint8Array(values) {
        this._uint8Ptr.set(values, this._offset >> 0 /* Uint8.Shift */);
        this._offset += values.length * (1 << 0 /* Uint8.Shift */);
    }
    /**
     * Writes a uint16 value
     * @param value Uint16 value to write
     */
    setUint16(value) {
        const uint16Offset = this._offset >> 1 /* Uint16.Shift */;
        this._uint16Ptr[uint16Offset] = value;
        if (!this.isDeviceLittleEndian)
            this.swap16Array(this._uint16Ptr, uint16Offset, 1);
        this._offset += 1 << 1 /* Uint16.Shift */;
    }
    /**
     * Writes a uint16 array
     * @param values Uint16 array to write
     */
    setUint16Array(values) {
        const uint16Offset = this._offset >> 1 /* Uint16.Shift */;
        this._uint16Ptr.set(values, uint16Offset);
        if (!this.isDeviceLittleEndian)
            this.swap16Array(this._uint16Ptr, uint16Offset, values.length);
        this._offset += values.length * (1 << 1 /* Uint16.Shift */);
    }
    /**
     * Writes a uint32 value
     * @param value Uint32 value to write
     */
    setUint32(value) {
        const uint32Offset = this._offset >> 2 /* Uint32.Shift */;
        this._uint32Ptr[uint32Offset] = value;
        if (!this.isDeviceLittleEndian)
            this.swap32Array(this._uint32Ptr, uint32Offset, 1);
        this._offset += 1 << 2 /* Uint32.Shift */;
    }
    /**
     * Writes a uint32 array
     * @param values Uint32 array to write
     */
    setUint32Array(values) {
        const uint32Offset = this._offset >> 2 /* Uint32.Shift */;
        this._uint32Ptr.set(values, uint32Offset);
        if (!this.isDeviceLittleEndian)
            this.swap32Array(this._uint32Ptr, uint32Offset, values.length);
        this._offset += values.length * (1 << 2 /* Uint32.Shift */);
    }
    /**
     * Writes a int32 value
     * @param value Int32 value to write
     */
    setInt32(value) {
        const int32Offset = this._offset >> 2 /* Int32.Shift */;
        this._int32Ptr[int32Offset] = value;
        if (!this.isDeviceLittleEndian)
            this.swap32Array(this._int32Ptr, int32Offset, 1);
        this._offset += 1 << 2 /* Int32.Shift */;
    }
    /**
     * Writes a int32 array
     * @param values Int32 array to write
     */
    setInt32Array(values) {
        const int32Offset = this._offset >> 2 /* Int32.Shift */;
        this._int32Ptr.set(values, int32Offset);
        if (!this.isDeviceLittleEndian)
            this.swap32Array(this._int32Ptr, int32Offset, values.length);
        this._offset += values.length * (1 << 2 /* Int32.Shift */);
    }
    /**
     * Writes a float32 value
     * @param value Float32 value to write
     */
    setFloat32(value) {
        const float32Offset = this._offset >> 2 /* Float32.Shift */;
        this._float32Ptr[float32Offset] = value;
        if (!this.isDeviceLittleEndian)
            this.swap32Array(this._float32Ptr, float32Offset, 1);
        this._offset += 1 << 2 /* Float32.Shift */;
    }
    /**
     * Writes a float32 array
     * @param values Float32 array to write
     */
    setFloat32Array(values) {
        const float32Offset = this._offset >> 2 /* Float32.Shift */;
        this._float32Ptr.set(values, float32Offset);
        if (!this.isDeviceLittleEndian)
            this.swap32Array(this._float32Ptr, float32Offset, values.length);
        this._offset += values.length * (1 << 2 /* Float32.Shift */);
    }
    /**
     * Writes a string as UTF-8 with 4 byte alignment padding
     * @param value String to write
     */
    setString(value) {
        const bytes = this._encoder.encode(value);
        const padding = AlignedDataSerializer.Padding(bytes.length);
        this.setUint32(bytes.length);
        this.setUint8Array(bytes);
        this._offset += padding;
    }
    /**
     * The number of bytes available
     */
    get bytesAvailable() {
        return this._arrayBuffer.byteLength - this._offset;
    }
    /**
     * Compute 4 byte alignment padding
     * @param offset Offset
     * @returns Padding
     */
    static Padding(offset) {
        return offset % 4 /* Constants.Alignment */ === 0 ? 0 : 4 /* Constants.Alignment */ - offset % 4 /* Constants.Alignment */;
    }
}
